import React, { useEffect, useMemo, useRef, useState } from 'react';
import Highcharts, { SeriesOptionsType } from 'highcharts';
import { HighchartsReact, HighchartsReactRefObject } from 'highcharts-react-official';
import ReactDOMServer from 'react-dom/server';
import { getSeriesYValues } from '../functions/main-chart/get-series-y-values';
import { mapAxisDataToSeries } from '../functions/main-chart/map-axis-data-to-series';
import { GridLineWidthStrategy, mapYAxis } from '../functions/main-chart/map-y-axis';
import {
    CrosshairPosition,
    TimelineAxisTypes,
    TimelineHighchartsReactRefObject,
    TimelineSeriesCustomData,
    TimelineYAxisCustomData,
} from '../shared';
import PointTooltip from './point-tooltip';
import { useEventListener } from '@hooks-clean/index';
import { TimelinePanelDto } from '@interfaces-clean/subject-monitoring.interface';
import { getThemeColours } from '@utils-clean/highchart-options/highchart-themes';
import { PanelTheme, TimelineDateSeriesType } from 'types/graphql-global-types';
import { useLocalState } from '@hooks/index';
import { getPanels_OpraQueries_panels_Panel } from '@queries/generated/central-monitoring.generated';

const NUMERIC_CHART_MIN_HEIGHT = 250;
const DATE_CHART_MIN_HEIGHT = 108;
const LANE_HEIGHT = 30;

interface IProps {
    ref?: React.Ref<HighchartsReactRefObject>;
    config: getPanels_OpraQueries_panels_Panel;
    title: string;
    data: TimelinePanelDto['string'];
    startDate: number;
    endDate: number;
    backgroundColor: string;
    borderColor: string;
    startingExtremes: { min: number; max: number };
    basisDate?: Date;
    synchronizeTooltips: (point: Highcharts.Point | undefined) => void;
    synchronizeCrosshairs: (pos: CrosshairPosition | undefined) => void;
}

const emptyRef = { current: null };

const MainChart = ({
    ref,
    config,
    title,
    data,
    startDate,
    endDate,
    backgroundColor,
    borderColor,
    startingExtremes,
    basisDate,
    synchronizeTooltips,
    synchronizeCrosshairs,
}: IProps) => {
    const chartRef = useRef<TimelineHighchartsReactRefObject>(null);
    const themeColors = useMemo(() => getThemeColours(config.panelTheme ?? PanelTheme.THEME_1), [config.panelTheme]);

    const { state } = useLocalState();

    // When mouse moves over chart, try to track position and draw crosshair
    useEventListener(chartRef.current?.container ?? emptyRef, 'mousemove', (e) => {
        const chart = chartRef.current?.chart;
        if (chart == null) {
            synchronizeCrosshairs(undefined);
            return;
        }

        // Get x position on plot area
        const plotX = e.offsetX - chart.plotLeft;
        // Make sure it's in-bounds
        if (plotX < 0 || plotX > chart.plotWidth) {
            synchronizeCrosshairs(undefined);
            return;
        }

        // Get min/max extreme of chart
        // It's an array, but it should presumably always have 1 element...
        const minExtremes = chart.xAxis.map((x) => x.getExtremes().min);
        const maxExtremes = chart.xAxis.map((x) => x.getExtremes().max);
        if (minExtremes.length === 0 || maxExtremes.length === 0) {
            synchronizeCrosshairs(undefined);
            return;
        }

        const minExtreme = Math.min(...minExtremes);
        const maxExtreme = Math.max(...maxExtremes);
        const extremesDelta = maxExtreme - minExtreme;

        // Get percentage of way x position is across the plot area.
        const percentage = plotX / chart.plotWidth;
        // Multiply the `extremesDelta` by this to get the value at the x position given the current extremes
        const value = new Date(minExtreme + Math.floor(extremesDelta * percentage));

        synchronizeCrosshairs({ plotX, value });
    });

    // When mouse leaves, remove crosshair
    useEventListener(chartRef.current?.container ?? emptyRef, 'mouseleave', () => {
        synchronizeCrosshairs(undefined);
    });

    const series: (SeriesOptionsType & TimelineSeriesCustomData)[] = useMemo(() => {
        const { leftAxis, rightAxis } = config.timeline!;

        const fieldDefinitions = state?.cm2Fields ?? [];

        function themeColorGenerator() {
            let i = 0;

            return () => themeColors[i++ % themeColors.length];
        }
        const getNextThemeColor = themeColorGenerator();

        function seriesIndexer() {
            let i = 0;

            return () => i++;
        }
        const getNextSeriesIndex = seriesIndexer();

        const pointEvents: Highcharts.PointEventsOptionsObject = {
            mouseOver() {
                synchronizeTooltips(this);
            },
            mouseOut() {
                synchronizeTooltips(undefined);
            },
        };

        const leftAxisSeries = mapAxisDataToSeries({
            axis: leftAxis,
            side: 'left',
            getNextThemeColor,
            getNextSeriesIndex,
            data,
            fieldDefinitions,
            pointEvents,
        });

        const rightAxisSeries =
            rightAxis != null
                ? mapAxisDataToSeries({
                      axis: rightAxis,
                      side: 'right',
                      getNextThemeColor,
                      getNextSeriesIndex,
                      data,
                      fieldDefinitions,
                      pointEvents,
                  })
                : [];

        return [...leftAxisSeries, ...rightAxisSeries];
    }, [state?.cm2Fields, config.timeline, themeColors, data, synchronizeTooltips]);

    const yAxis: (Highcharts.YAxisOptions & TimelineYAxisCustomData)[] = useMemo(() => {
        const { leftAxis, rightAxis } = config.timeline!;

        // Strategy for getting width of gridlines based on axis series type
        function createGridLineWidthStrategy() {
            let calledPreviouslyForNumeric = false;

            // Date series axis never have grid lines (left or right)
            // The first numeric series axis has grid lines
            // - If both left and right axes are numeric, only the left has grid lines
            return ((type: 'numeric' | 'date') => {
                if (type === 'date' || calledPreviouslyForNumeric) {
                    return 0;
                }

                calledPreviouslyForNumeric = true;
                return 1;
            }) satisfies GridLineWidthStrategy;
        }
        const gridLineWidthStrategy = createGridLineWidthStrategy();

        const leftSeries = series.filter((x) => x.yAxis === 'left');
        const rightSeries = series.filter((x) => x.yAxis === 'right');

        return [
            mapYAxis({ axis: leftAxis, side: 'left', series: leftSeries, gridLineWidthStrategy }),
            mapYAxis({ axis: rightAxis, side: 'right', series: rightSeries, gridLineWidthStrategy }),
        ];
    }, [config, series]);

    const chartHeight = useMemo(() => {
        // Get minimum height required to fit the y-axis title `text`
        // The `title` and `unit` can break onto separate lines, so it only needs to be big enough to fit the larger one
        const longestYAxisTitleLength = Math.max(
            0,
            ...yAxis.map((y) => y.title?.title ?? '').map((t) => t.length),
            // +1 since more space is needed for the brackets `({unit})`
            ...yAxis.map((y) => y.title?.unit ?? '').map((t) => t.length + 1),
        );
        const minHeightForTitle = longestYAxisTitleLength * 13;

        const hasNumericSeriesWithData = series.some((s) => {
            const hasData = 'data' in s && Array.isArray(s.data) && s.data.length > 0;

            return s.custom?.axisType === TimelineAxisTypes.Numeric && hasData;
        });

        // Numeric series require a larger minHeight, so if either y axes contain any numeric series, use larger minHeight
        const minHeight = Math.max(
            hasNumericSeriesWithData ? NUMERIC_CHART_MIN_HEIGHT : DATE_CHART_MIN_HEIGHT,
            minHeightForTitle,
        );

        const dateSeries = series.filter((s) => s.custom.axisType === TimelineAxisTypes.Date);

        // Only date series require varying height. If only numeric series are included, use fixed height
        if (dateSeries.length === 0) {
            return minHeight;
        }

        // Get distinct y values from date series. This is how many lanes it has
        const lanes = new Set(dateSeries.flatMap(getSeriesYValues)).size;

        return Math.max(minHeight, lanes * LANE_HEIGHT);
    }, [yAxis, series]);

    const allowSharedTooltips = useMemo(() => {
        // If either axis is a Date Range, we don't want to allow shared tooltips, as they look bad.
        if (
            config.timeline?.leftAxis.dateSeries?.some((s) => s.type === TimelineDateSeriesType.DATE_RANGE) ||
            config.timeline?.rightAxis?.dateSeries?.some((s) => s.type === TimelineDateSeriesType.DATE_RANGE)
        ) {
            return false;
        }
        return true;
    }, [config.timeline?.leftAxis.dateSeries, config.timeline?.rightAxis?.dateSeries]);

    // On mount, get `plotLeft` property of chart. This is so that the title doesn't overlap with the left y-axis labels
    const [chartPlotLeft, setChartPlotLeft] = useState<number>();
    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setChartPlotLeft(chartRef?.current?.chart.plotLeft);
    }, []);

    const options: Highcharts.Options = useMemo(
        () => ({
            colors: themeColors,
            title: {
                text: title,
                align: 'left',
                x: chartPlotLeft,
                floating: false,
                style: {
                    fontWeight: 'normal',
                    fontSize: 16.38,
                    fontFamily: 'var(--kendo-font-family, inherit)',
                    color: '#3E536E',
                },
            },
            chart: {
                alignTicks: false,
                inverted: false,
                animation: false,
                zooming: {
                    mouseWheel: false,
                },
                height: chartHeight,
                marginTop: 0,
                marginBottom: 0,
                style: {
                    justifySelf: 'center',
                },
                plotBackgroundColor: backgroundColor,

                events: {
                    load() {
                        const chart = this;

                        chart.axes
                            .filter((x) => x.isXAxis)
                            .forEach((x) => {
                                x.setExtremes(startingExtremes.min, startingExtremes.max);
                            });
                    },
                },
            },
            plotOptions: {
                xrange: {
                    grouping: false,
                    dataLabels: { enabled: false },
                    colorByPoint: false,
                },
            },
            yAxis: yAxis.map((axis) => ({
                ...axis,
                lineColor: borderColor,
            })),
            xAxis: {
                panningEnabled: false,
                type: 'datetime',
                min: startDate,
                max: endDate,
                crosshair: false,
                ordinal: false,
                visible: false,
            },
            series,
            tooltip: {
                split: false,
                shared: allowSharedTooltips,
                animation: false,
                useHTML: true,
                formatter() {
                    return ReactDOMServer.renderToString(
                        <PointTooltip point={this} basisDate={basisDate} chartHeight={chartHeight} />,
                    );
                },
            },
            credits: { enabled: false },
            rangeSelector: { enabled: false },
            exporting: { enabled: false },
            navigator: { enabled: false },
            scrollbar: { enabled: false },
        }),
        [
            title,
            allowSharedTooltips,
            backgroundColor,
            basisDate,
            borderColor,
            chartHeight,
            endDate,
            series,
            startDate,
            startingExtremes.max,
            startingExtremes.min,
            themeColors,
            yAxis,
            chartPlotLeft,
        ],
    );

    return (
        <HighchartsReact
            ref={(el) => {
                if (ref != null) {
                    typeof ref === 'function' ? ref(el) : (ref.current = el);
                }
                chartRef.current = el;
            }}
            highcharts={Highcharts}
            constructorType="stockChart"
            options={options}
        />
    );
};

export default MainChart;
